/**
 * Event bus for the add-to-cart flight.
 *
 * The ghost is drawn by `<FlyToCartLayer/>` in a portal on `<body>`, not by the
 * card that fired it: the item modal navigates home on "Adicionar", so anything
 * rendered inside the source component would unmount mid-flight. Callers hand
 * over only the element the item is flying FROM — the layer resolves the target,
 * the geometry and the artwork itself.
 */

/** A snapshot of the source element, taken before it can unmount. */
export type FlyOrigin = {
  /** Viewport centre of the source element. */
  centerX: number;
  centerY: number;
  /** Source box, used to pick a sensible ghost size. */
  width: number;
  height: number;
  /** The exact bitmap the customer is looking at (already decoded/cached). */
  imageSrc?: string;
};

/** Which way the cart moved — the CTA answers with a different bounce. */
export type CartBumpKind = "add" | "remove";

type FlyListener = (origin: FlyOrigin) => void;
type BumpListener = (kind: CartBumpKind) => void;

const flyListeners = new Set<FlyListener>();
const bumpListeners = new Set<BumpListener>();

/** Marks the element a flight lands on (the bag icon in the cart CTA). */
export const CART_TARGET_ATTR = "data-cart-target";

/** Marks an element a flight can take off from (an item's photo). */
export const FLY_ORIGIN_ATTR = "data-fly-origin";

/**
 * Value of {@link FLY_ORIGIN_ATTR} on the item modal's hero image. The modal
 * also renders related-item cards, which are origins too, so the CTA must name
 * the one it means instead of taking the first in the DOM.
 */
export const FLY_ORIGIN_ITEM_DETAIL = "item-detail";

/**
 * Starts a flight from `element`. Reads the rect and the image synchronously —
 * by the time the animation ends the element may be gone.
 */
export function flyToCart(element: Element | null | undefined): void {
  if (!element || flyListeners.size === 0) return;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const img = element.querySelector("img");
  const origin: FlyOrigin = {
    centerX: rect.x + rect.width / 2,
    centerY: rect.y + rect.height / 2,
    width: rect.width,
    height: rect.height,
    imageSrc: img?.currentSrc || img?.src || undefined,
  };

  flyListeners.forEach((listener) => listener(origin));
}

export function subscribeFlyToCart(listener: FlyListener): () => void {
  flyListeners.add(listener);
  return () => {
    flyListeners.delete(listener);
  };
}

/**
 * Fired when a ghost lands, and when an item leaves the bag — the cart CTA
 * answers with its bounce (outwards on an add, inwards on a removal).
 */
export function emitCartBump(kind: CartBumpKind = "add"): void {
  bumpListeners.forEach((listener) => listener(kind));
}

export function subscribeCartBump(listener: BumpListener): () => void {
  bumpListeners.add(listener);
  return () => {
    bumpListeners.delete(listener);
  };
}
